import * as api from "./client";

// ============================================================================
// Types
// ============================================================================

export type TemplateType = "survey_request" | "reminder";

export type TemplateStatus = "configured" | "unconfigured" | "archived";

export type MailingTemplate = {
  /** Local template uuid referencing `campaign.template.id`; `null` when no local row exists. */
  id: string | null;
  sendgrid_template_id: string;
  name: string | null;
  subject: string | null;
  updated_at: string | null;
  type: TemplateType | null;
  archived: boolean;
  status: TemplateStatus;
  /**
   * `true` for a config row whose SendGrid template was deleted — an orphan.
   * Such rows have no live SendGrid metadata (subject / updated_at) and can't
   * be previewed or re-typed.
   */
  missing_in_sendgrid: boolean;
};

export type TemplateCounts = {
  total: number;
  configured: number;
  unconfigured: number;
  archived: number;
  missing_in_sendgrid: number;
};

export type TemplateListResponse = {
  templates: MailingTemplate[];
  counts: TemplateCounts;
  request_id?: string;
};

export type TemplateListFilters = {
  type?: TemplateType;
  onlyUnconfigured?: boolean;
};

export type SetTemplateConfigInput = {
  /** Pass `null` to clear the type assignment. */
  type?: TemplateType | null;
  archived?: boolean;
};

export type SetTemplateConfigResponse = {
  template: {
    sendgrid_template_id: string;
    type: TemplateType | null;
    archived: boolean;
    status: TemplateStatus;
  };
  request_id?: string;
};

export type TemplatePreviewResponse = {
  subject: string;
  html_content: string;
  request_id?: string;
};

// ============================================================================
// Service
// ============================================================================

function buildListParams(filters?: TemplateListFilters): string {
  const params = new URLSearchParams();
  if (filters?.type) params.append("type", filters.type);
  if (filters?.onlyUnconfigured) params.append("onlyUnconfigured", "true");
  const query = params.toString();
  return query ? `?${query}` : "";
}

export const templates = {
  list: async (filters?: TemplateListFilters): Promise<TemplateListResponse> => {
    return api.get<TemplateListResponse>(`/v1/templates${buildListParams(filters)}`);
  },

  setConfig: async (
    sendgridTemplateId: string,
    input: SetTemplateConfigInput,
  ): Promise<SetTemplateConfigResponse> => {
    return api.patch<SetTemplateConfigResponse>(
      `/v1/templates/${encodeURIComponent(sendgridTemplateId)}`,
      input,
    );
  },

  preview: async (sendgridTemplateId: string): Promise<TemplatePreviewResponse> => {
    return api.get<TemplatePreviewResponse>(
      `/v1/templates/${encodeURIComponent(sendgridTemplateId)}/preview`,
    );
  },
};
